import { useState, useCallback, useEffect } from 'react';
import { useDebounce } from '@/hooks/useDebounce';
import { HybridSearchEngine } from '@/services/hybridSearchService';
import { HybridSearchResult, SearchContext, ContextualIcon } from '@/types/search';
import { ContextualSearchEngine } from '@/services/contextualSearch';

export const useHybridSearch = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<HybridSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchContext, setSearchContext] = useState<SearchContext | null>(null);
  const [contextualIcons, setContextualIcons] = useState<ContextualIcon[]>([]);
  
  const debouncedQuery = useDebounce(query, 300);

  // Phase 1: Détection contextuelle instantanée (0ms)
  useEffect(() => {
    if (query.length > 0) {
      const context = ContextualSearchEngine.parseSearchIntent(query);
      const icons = ContextualSearchEngine.generateContextualIcons(query);
      
      setSearchContext(context);
      setContextualIcons(icons);
    } else {
      setSearchContext(null);
      setContextualIcons([]);
    }
  }, [query]);

  // Phase 2: Recherche hybride (debounced)
  const performSearch = useCallback(async (searchQuery: string) => {
    if (searchQuery.length < 2) {
      setResults([]);
      return;
    }

    setIsLoading(true);
    try {
      const hybridResults = await HybridSearchEngine.search(searchQuery, {
        similarityThreshold: 0.3,
        resultLimit: 10,
      });

      setResults(hybridResults);
    } catch (error) {
      console.error('Erreur recherche hybride:', error);
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debouncedQuery) {
      performSearch(debouncedQuery);
    } else {
      setResults([]);
    }
  }, [debouncedQuery, performSearch]);

  const clearSearch = useCallback(() => {
    setQuery('');
    setResults([]);
    setSearchContext(null);
    setContextualIcons([]);
  }, []);

  return {
    query,
    setQuery,
    results,
    isLoading,
    searchContext,
    contextualIcons,
    clearSearch,
  };
};
